import { AttributionLink } from '@components/attribution-link'
import { HamburgerButton } from '@components/hamburger-button'
import { Logo } from '@components/icons/logo'
import { DropdownMenu } from '@components/layout/dropdown-menu'
import { Container } from '@components/layout/grid'
import { MAIN_NAVIGATION_LINKS } from '@components/layout/navigation-links'
import { Desktop, Mobile } from '@components/responsive'
import { Text } from '@components/text'
import { useBodyLock } from '@lib/hooks/useBodyLock'
import { useIsMobile } from '@lib/hooks/useMedia'
import { navigationStore } from '@lib/store/ui/navigation'
import { LocalLink } from '@shared/util/link'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import type { FunctionComponent } from 'react'

import HeaderActionButton from './actions/header-action-button'
import styles from './header.module.scss'

const Sidebar = dynamic(() => import('./sidebar'), {
    ssr: false,
})

export const Header: FunctionComponent<{
    hasBanner?: boolean
    hideNavigation?: boolean
}> = observer(({ hasBanner = false, hideNavigation = false }) => {
    const [showHeader, setShowHeader] = useState(true)
    const outerRef = useRef<HTMLDivElement>(null)
    const ref = useRef<HTMLDivElement>(null)
    const isMobile = useIsMobile()

    const router = useRouter()

    // close mobile nav when route changes
    useEffect(() => {
        navigationStore.set({
            isOpen: false,
        })
    }, [router.pathname])

    // Hide header for pages /bridge & /marketplace on mobile devices
    useEffect(() => {
        if (
            isMobile &&
            (router.asPath.includes('bridge') ||
                router.asPath.includes('marketplace'))
        ) {
            setShowHeader(false)
        } else {
            setShowHeader(true)
        }
    }, [isMobile, router.asPath, router.pathname])

    // lock scroll when mobile nav is open
    useBodyLock(navigationStore.isOpen)

    // handle header position when banner is present
    useEffect(() => {
        const handleScroll = () => {
            if (!hasBanner || !outerRef.current || !ref.current) return

            const outerRect = outerRef.current.getBoundingClientRect()

            const outerTop = outerRect.top + window.scrollY
            const refDistanceToTop = outerTop - window.scrollY

            if (refDistanceToTop > 0 && refDistanceToTop < outerTop) {
                ref.current.style.top = `${refDistanceToTop}px`
            } else if (refDistanceToTop <= 0) {
                ref.current.style.top = '0px'
            } else {
                ref.current.style.top = `${outerTop}px`
            }
        }

        if (hasBanner) {
            handleScroll()
            window.addEventListener('scroll', handleScroll)
        } else {
            // account for when user manually closes banner
            if (ref.current) ref.current.style.top = '0px'
            window.removeEventListener('scroll', handleScroll)
        }

        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [hasBanner])

    const handleCloseClick = () => {
        navigationStore.set({
            isOpen: false,
        })
    }

    const content = (
        <>
            <div
                ref={ref}
                className={styles.wrapper}
                data-testid='layout.header'
            >
                <nav className={styles.header}>
                    <Container>
                        <div className={styles.inner}>
                            <AttributionLink href='/'>
                                <Logo
                                    width='52'
                                    height='42'
                                    alt='Step logo'
                                    preset='color'
                                />
                            </AttributionLink>

                            {!hideNavigation && (
                                <div className={styles.right}>
                                    <Desktop
                                        visible={navigationStore.showLinks}
                                    >
                                        <div
                                            className={classNames(styles.list)}
                                        >
                                            {MAIN_NAVIGATION_LINKS.map(
                                                (route) => {
                                                    const {
                                                        label,
                                                        href,
                                                        items,
                                                    } = route

                                                    if (
                                                        items &&
                                                        items?.length > 0
                                                    ) {
                                                        return (
                                                            <DropdownMenu
                                                                key={label}
                                                                id={label}
                                                                items={items.map(
                                                                    ({
                                                                        label,
                                                                        href,
                                                                    }) => ({
                                                                        text: label,
                                                                        url: new LocalLink(
                                                                            {
                                                                                base: href,
                                                                            },
                                                                        ),
                                                                    }),
                                                                )}
                                                                label={label}
                                                            />
                                                        )
                                                    }

                                                    const isActive =
                                                        router.pathname === href

                                                    return (
                                                        <AttributionLink
                                                            key={label}
                                                            href={href}
                                                            className={
                                                                styles.link
                                                            }
                                                        >
                                                            <Text
                                                                tag='span'
                                                                typeStyle={{
                                                                    _: 'body-2xs',
                                                                    md: 'body-xs',
                                                                }}
                                                                bold
                                                                color={
                                                                    isActive
                                                                        ? 'primary'
                                                                        : 'text'
                                                                }
                                                            >
                                                                {label}
                                                            </Text>
                                                        </AttributionLink>
                                                    )
                                                },
                                            )}
                                        </div>
                                    </Desktop>

                                    <div className={styles.buttons}>
                                        {!router.asPath.startsWith(
                                            '/earlypay-get-paid-before-payday',
                                        ) && (
                                            <div
                                                className={
                                                    styles['action-button']
                                                }
                                            >
                                                <HeaderActionButton />
                                            </div>
                                        )}

                                        {navigationStore.showLinks && (
                                            <Mobile>
                                                <HamburgerButton
                                                    open={
                                                        navigationStore.isOpen
                                                    }
                                                    onClick={() =>
                                                        navigationStore.set({
                                                            isOpen: !navigationStore.isOpen,
                                                        })
                                                    }
                                                />
                                            </Mobile>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Container>
                </nav>

                {navigationStore.showLinks && (
                    <Sidebar
                        links={MAIN_NAVIGATION_LINKS}
                        open={navigationStore.isOpen}
                        onOverlayClick={handleCloseClick}
                    />
                )}
            </div>
        </>
    )

    if (!showHeader) {
        return <header ref={outerRef} />
    }

    return <header ref={outerRef}>{content}</header>
})

export default Header
